import { useSelector } from '@xstate/react';
import cn from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Checkbox } from '@webapp/common/components/checkbox';
import { useTextStyle } from '@webapp/common/components/custom-styles';
import { CssPageBlock, CssQuestionType, CssUiComponent } from '@webapp/common/components/survey-custom';
import { Textfield } from '@webapp/common/components/textfield';
import { setHtml } from '@webapp/common/lib/ui';
import { CANTRATE_ID } from '@webapp/common/resources/constants';

import type { SurveyQuestion } from '@webapp/survey/src/components/questions/lib';
import type { QuestionAnswerActor } from '@webapp/survey/src/entities/answer-machine';
import { selectIsWithAudioRecogn } from '@webapp/survey/src/machine/selectors';
import { usePipingAnswersReplacer } from '@webapp/survey/src/machine/use-piping-answers-replacer';
import { useStateSelector } from '@webapp/survey/src/machine/use-state-selector';

import css from './group-free-answers.css';

const GroupFreeAnswersRow: VFC<{
    actor: QuestionAnswerActor;
    questionId: number;
    cantRate: boolean;
    cantRateText: string;
}> = ({ actor, questionId, cantRate, cantRateText }) => {
    const isWithAudioRecogn = useStateSelector(selectIsWithAudioRecogn);
    const pipingAnswersReplacer = usePipingAnswersReplacer();
    const { answerStyle, inputStyle, itemsGap, textStyle } = useTextStyle();

    const { id, response, value, hidden, hiddenByAnswersHide, forwardParent } = useSelector(actor, (a) => a.context);
    const { value: responseValue } = useSelector(response, (r) => r.context);
    const [forwardParentActor, setForwardParentActor] = useState<undefined | QuestionAnswerActor>();
    const forwardParentContext = useSelector(forwardParentActor, (s) => s?.context);

    const forwardParentValue = useSelector(forwardParentContext?.response, (s) => s?.context.extra);
    const questions = useStateSelector((s) => s.questions);
    useEffect(() => {
        if (!forwardParent && !forwardParentActor) return undefined;

        questions.forEach((q) => {
            const founded = q.getSnapshot().context.answersActors.find((a) => {
                return a.getSnapshot().context.id === forwardParent;
            });
            if (founded) {
                setForwardParentActor(founded);
            }
        });
    }, [questions, forwardParent]);

    const cantRateChecked = responseValue === CANTRATE_ID;

    const rowStyle = useMemo(
        () => ({
            ...answerStyle,
            rowGap: itemsGap,
        }),
        [answerStyle, itemsGap],
    );

    const handleCantRate = useCallback(
        (checked: boolean) => {
            response.send({ type: 'CHANGE', value: checked ? CANTRATE_ID : '', questionId });
        },
        [response, questionId],
    );

    if (hidden === true || hiddenByAnswersHide === true) return null;

    return (
        <div className={cn(CssPageBlock.ANSWER, css.answer)} key={id} style={rowStyle}>
            <div
                className={cn(CssUiComponent.LABEL, css.label)}
                style={textStyle}
                {...setHtml(pipingAnswersReplacer(forwardParentValue ? forwardParentValue.toString() : value[0]))}
            />
            <Textfield
                withSpeechRecognition={isWithAudioRecogn}
                transparent
                rootClassName={css.field}
                style={inputStyle}
                value={cantRateChecked ? '' : (responseValue as string) || ''}
                onChange={(v: any): void => {
                    void response.send({ type: 'CHANGE', value: v, questionId });
                }}
            />
            {cantRate && (
                <div style={{ gridColumn: '1/-1', marginBottom: '20px' }}>
                    <Checkbox checked={cantRateChecked} label={cantRateText} onChange={handleCantRate} />
                </div>
            )}
        </div>
    );
};

export const GroupFreeAnswers: VFC<SurveyQuestion> = ({
    question: {
        answersActors,
        id: questionId,
        params: { cantRate, cantRateText },
    },
    questionBlockPosition,
}) => {
    const { listStyle } = useTextStyle();

    return (
        <div
            className={cn(CssQuestionType.QUESTION, CssQuestionType.GROUP_FREE_ANSWERS, css.list)}
            style={{ ...listStyle, justifyContent: questionBlockPosition }}
        >
            {answersActors.map((actor: QuestionAnswerActor) => (
                <GroupFreeAnswersRow
                    actor={actor}
                    key={actor.id}
                    questionId={questionId}
                    cantRate={cantRate}
                    cantRateText={cantRateText}
                />
            ))}
        </div>
    );
};
GroupFreeAnswers.displayName = 'GroupFreeAnswers';
